import { goalsApi } from "@/api/goals.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import useFormatter, { getNameInitials } from "@/hooks/use-formatter.hook.ts";
import { employeeGoalsKeys } from "@/queries/employees/use-employee-goals.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { getEmployeeAvatarUrl } from "@/types/employees/employee.types.ts";
import {
  ApiGoal,
  ApiGoalCheckInPayload,
  ApiGoalKeyResultPayload,
  ApiGoalOwnerType,
  ApiGoalPayload,
  ApiGoalSearchFilter,
} from "@/types/goals.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { SelectItem } from "@skailer-com/uikit";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

export const goalsKeys = createQueryKeys("goals", {
  search: (filter: ApiGoalSearchFilter) => ({
    queryKey: [filter],
    queryFn: async () => {
      const result = await goalsApi.search(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  strategic: () => ({
    queryKey: ["strategic"],
    queryFn: async () => {
      const result = await goalsApi.getGoalsStrategic();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  strategicSearch: (filter: ApiGoalSearchFilter) => ({
    queryKey: ["strategic-search", filter],
    queryFn: async () => {
      const result = await goalsApi.searchStrategic(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  counts: () => ({
    queryKey: ["counts"],
    queryFn: async () => {
      const result = await goalsApi.getGoalsCount();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  detail: (id: number) => ({
    queryKey: [id],
    queryFn: async () => {
      const result = await goalsApi.getGoal(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  checkIns: (goalId: number, pageNumber: number) => ({
    queryKey: [goalId, pageNumber],
    queryFn: async () => {
      const result = await goalsApi.searchCheckIns(goalId, { page_number: pageNumber });
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useGoalsSearch = (filter: ApiGoalSearchFilter, enabled: boolean = true) => {
  return useQuery({
    ...goalsKeys.search(filter),
    enabled,
  });
};

export const useGoalsStrategic = (enabled: boolean = true) => {
  return useQuery({
    ...goalsKeys.strategic(),
    enabled,
  });
};

export const useGoalsStrategicSearch = (filter: ApiGoalSearchFilter, enabled: boolean = true) => {
  return useQuery({
    ...goalsKeys.strategicSearch(filter),
    enabled,
  });
};

export const useGoalsCounts = (enabled: boolean = true) => {
  return useQuery({
    ...goalsKeys.counts(),
    enabled,
  });
};

export const useGoal = (id: number, enabled: boolean = true) => {
  return useQuery({
    ...goalsKeys.detail(id),
    enabled,
    staleTime: 60 * 60 * 1000,
  });
};

export const useCheckInsSearch = (
  goalId: number,
  pageNumber: number = 1,
  enabled: boolean = true
) => {
  return useQuery({
    ...goalsKeys.checkIns(goalId, pageNumber),
    enabled,
  });
};

export const useGoalsSearchInline = (enabled = true) => {
  const { renderEmployeeName } = useFormatter();
  const [filter, setFilter] = useState<Partial<ApiGoalSearchFilter>>({});
  const { data, isLoading } = useGoalsSearch(filter as ApiGoalSearchFilter, enabled);

  const options = useMemo(() => {
    return (
      data?.items?.map((g) => {
        let avatarUrl: string | undefined;
        let emoji: string | undefined;
        let acronym: string | undefined;
        let caption: string | undefined;

        switch (g.owner_type) {
          case ApiGoalOwnerType.employee:
            avatarUrl = g.owner_employee ? getEmployeeAvatarUrl(g.owner_employee, "md") : undefined;
            caption = g.owner_employee ? renderEmployeeName(g.owner_employee) : undefined;
            acronym = caption ? getNameInitials(caption) : undefined;
            break;
          case ApiGoalOwnerType.department:
            avatarUrl = g.owner_department?.image_url ?? undefined;
            emoji = g.owner_department?.emoji ?? undefined;
            acronym = g.owner_department?.title;
            caption = g.owner_department?.title;
            break;
          case ApiGoalOwnerType.team:
            emoji = g.owner_team?.emoji ?? undefined;
            acronym = g.owner_team?.title;
            caption = g.owner_team?.title;
            break;
          case ApiGoalOwnerType.company:
            avatarUrl = g.owner_company?.logo_url ?? undefined;
            acronym = g.owner_company?.title;
            caption = g.owner_company?.title;
            break;
        }

        return {
          id: g.id,
          title: g.title,
          avatarUrl,
          emoji,
          acronym,
          caption,
        } as SelectItem;
      }) || ([] as SelectItem[])
    );
  }, [data, renderEmployeeName]);

  function search(text: string | undefined) {
    setFilter({ ...filter, text });
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

const invalidateGoalSidebarCounters = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: goalsKeys.counts._def });
};

interface GoalSaveParams {
  id?: number;
  payload: ApiGoalPayload;
}

export const useGoalSave = <T extends FieldValues>(control: Control<T> | undefined = undefined) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: GoalSaveParams) => {
      const result = id
        ? await goalsApi.updateGoal(id, payload)
        : await goalsApi.createGoal(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: goalsKeys.detail(data.id).queryKey });
      invalidateGoalSidebarCounters(queryClient);
      if (data.owner_type === ApiGoalOwnerType.employee && data.owner_employee?.id) {
        queryClient.invalidateQueries({
          queryKey: employeeGoalsKeys.search._def,
          predicate: (query) => query.queryKey[2] === data.owner_employee?.id,
        });
      }
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useGoalDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, behavior = "move" }: { id: number; behavior?: "move" | "delete" }) => {
      const goal = queryClient.getQueryData<ApiGoal>(goalsKeys.detail(id).queryKey);
      const result = await goalsApi.deleteGoal(id, behavior);
      if (result.error) throw new ApiErrorException(result.error);
      return { data: result.data, goal };
    },
    onSuccess: ({ goal }) => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.search._def });
      invalidateGoalSidebarCounters(queryClient);

      if (goal?.owner_type === ApiGoalOwnerType.employee && goal.owner_employee?.id) {
        queryClient.invalidateQueries({
          queryKey: employeeGoalsKeys.search._def,
          predicate: (query) => query.queryKey[2] === goal.owner_employee?.id,
        });
      }
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

interface KeyResultSaveParams {
  goalId: number;
  keyResultId?: number;
  payload: ApiGoalKeyResultPayload;
}

export const useKeyResultSave = <T extends FieldValues>(
  control: Control<T> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ goalId, keyResultId, payload }: KeyResultSaveParams) => {
      const result = keyResultId
        ? await goalsApi.updateKeyResult(goalId, keyResultId, payload)
        : await goalsApi.createKeyResult(goalId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: goalsKeys.detail(variables.goalId).queryKey });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

interface KeyResultDeleteParams {
  goalId: number;
  keyResultId: number;
}

export const useKeyResultDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ goalId, keyResultId }: KeyResultDeleteParams) => {
      const result = await goalsApi.deleteKeyResult(goalId, keyResultId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: goalsKeys.detail(variables.goalId).queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

interface CheckInSaveParams {
  goalId: number;
  checkInId?: number;
  payload: ApiGoalCheckInPayload;
}

interface CheckInDeleteParams {
  goalId: number;
  checkInId: number;
}

export const useCheckInSave = <T extends FieldValues>(
  control: Control<T> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ goalId, checkInId, payload }: CheckInSaveParams) => {
      const result = checkInId
        ? await goalsApi.updateCheckIn(goalId, checkInId, payload)
        : await goalsApi.createCheckIn(goalId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: goalsKeys.detail(variables.goalId).queryKey });
      queryClient.invalidateQueries({ queryKey: goalsKeys.checkIns._def });
      invalidateGoalSidebarCounters(queryClient);
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useCheckInDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ goalId, checkInId }: CheckInDeleteParams) => {
      const result = await goalsApi.deleteCheckIn(goalId, checkInId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: goalsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: goalsKeys.detail(variables.goalId).queryKey });
      queryClient.invalidateQueries({ queryKey: goalsKeys.checkIns._def });
      invalidateGoalSidebarCounters(queryClient);
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
